import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";
import ServerErrorAlert from "../profiles/ServerErrorAlert";

const QUOTE_TEXT_MAX_LENGTH = 2000;

const QUOTE_ERROR_LABELS = { quote_text: "Quote" };

// The Add/edit form for a single quote -- a Card matching Quote's own chassis, swapped in for the
// display card while adding or editing. `onSave` is expected to THROW on failure (the quotes.js client
// always throws on a non-2xx or a network rejection via throwOnApiError), which is what keeps the
// editor open with the entered text intact rather than closing on a save that didn't happen.
function QuoteEditor({ initialQuoteText = "", onSave, onClose, disabled = false, autoFocus = false }) {
  const [quoteText, setQuoteText] = useState(initialQuoteText ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  // Focus on mount so the visitor can type at once; cursor to the end rather than selecting any text.
  useEffect(() => {
    if (!autoFocus || !textareaRef.current) return;
    const textarea = textareaRef.current;
    textarea.focus();
    const end = textarea.value.length;
    textarea.setSelectionRange(end, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trimmed client-side (mirrors the server's own strippable :quote_text) so a whitespace-only entry
  // can't pass the required check, and so the eventual server length validation sees the same string
  // this form is validating against.
  const trimmed = quoteText.trim();
  const invalid = trimmed.length === 0 || trimmed.length > QUOTE_TEXT_MAX_LENGTH;

  const handleSave = async () => {
    if (invalid || saving || disabled) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ quote_text: trimmed });
      onClose();
    } catch (e) {
      // Editor stays open, entered text is untouched (quoteText state is never reset here), and the
      // error renders inline -- a failed save must never look like a save that happened.
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <ServerErrorAlert generalError={error} labelMap={QUOTE_ERROR_LABELS} />
        <textarea
          ref={textareaRef}
          className="form-control"
          rows={4}
          maxLength={QUOTE_TEXT_MAX_LENGTH}
          placeholder="Add a quote…"
          aria-label="Quote"
          value={quoteText}
          onChange={(event) => setQuoteText(event.currentTarget.value)}
          disabled={saving || disabled}
        />
      </Card.Body>
      <Card.Footer className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-flex me-2"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-flex"
          onClick={handleSave}
          disabled={invalid || saving || disabled}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </Card.Footer>
    </Card>
  );
}

QuoteEditor.propTypes = {
  initialQuoteText: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

export default QuoteEditor;
